import { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet-draw';
import 'leaflet-draw/dist/leaflet.draw.css';
import { Circle, Navigation, MapPin, Hexagon, Trash2, Layers, X, Minimize2, Maximize2, ExternalLink, ImageIcon, Loader2 } from 'lucide-react';
import { cn, API_BASE_URL, SOCKET_URL } from '../../lib/utils';
import type { Incident } from '../../types';
import { useAppSelector } from '../../store';
import { useActiveEvent } from '../../contexts/EventContext';
import { getEventMapCenter } from '../../lib/eventLocation';
import { emergencyMatchesEvent } from '../../lib/emergencyLocation';
import MapOverlayControls from './MapOverlayControls';
import {
  type EventMapOverlay,
  type EventMapOverlaySavePayload,
  type RotatedOverlayLayer,
  computeOverlayCorners,
  createOverlayLayer,
  updateOverlayLayer,
} from '../../lib/mapOverlay';
import { useEventMapOverlay } from '../../hooks/useEventMapOverlay';
import { useEventMapDrawings } from '../../hooks/useEventMapDrawings';
import type { EventMapDrawing } from '../../lib/mapDrawings';
import { io, Socket } from 'socket.io-client';
import { MAP_ZOOM_RESOURCE_EVENT, type MapZoomResourceDetail } from '../../lib/mapCommandEvents';

// Fix default marker icons
delete (L.Icon.Default.prototype as unknown as Record<string, unknown>)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.3/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.3/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.3/dist/images/marker-shadow.png',
});

const severityColors: Record<string, string> = {
  critical: '#ef4444', high: '#f97316', medium: '#eab308', low: '#22c55e', info: '#3b82f6',
};

function createIncidentIcon(severity: string) {
  const color = severityColors[severity] || '#64748b';
  return L.divIcon({
    className: 'custom-incident-marker',
    html: `<div style="width:14px;height:14px;border-radius:50%;background:${color};border:2px solid #fff;box-shadow:0 0 8px ${color}80;"></div>`,
    iconSize: [14, 14],
    iconAnchor: [7, 7],
  });
}

type DrawingMode = 'none' | 'marker' | 'circle' | 'polyline' | 'polygon';

const DEFAULT_DRAWING_COLORS: Record<string, string> = {
  marker: '#2563eb',
  circle: '#2563eb',
  polyline: '#f97316',
  polygon: '#7c3aed',
};

function defaultDrawingColor(type: DrawingMode): string {
  return DEFAULT_DRAWING_COLORS[type] || '#2563eb';
}

function createColoredMarkerIcon(color: string) {
  return L.divIcon({
    className: 'custom-map-marker',
    html: `<div style="width:18px;height:18px;border-radius:50%;background:${color};border:3px solid #fff;box-shadow:0 0 10px ${color}99;"></div>`,
    iconSize: [18, 18],
    iconAnchor: [9, 9],
  });
}

function applyDrawingColor(layer: L.Layer, type: DrawingMode, color: string) {
  if (type === 'marker') {
    (layer as L.Marker).setIcon(createColoredMarkerIcon(color));
  } else if (type === 'circle') {
    (layer as L.Circle).setStyle({ color, fillColor: color });
  } else if (type === 'polyline') {
    (layer as L.Polyline).setStyle({ color });
  } else if (type === 'polygon') {
    (layer as L.Polygon).setStyle({ color, fillColor: color });
  }
}

function readDrawingColor(drawing: { type: DrawingMode; color?: string; layer: L.Layer }): string {
  if (drawing.color) return drawing.color;

  if (drawing.type === 'marker') {
    return defaultDrawingColor('marker');
  }
  if (drawing.type === 'circle') {
    return (drawing.layer as L.Circle).options.color || defaultDrawingColor('circle');
  }
  if (drawing.type === 'polyline') {
    return (drawing.layer as L.Polyline).options.color || defaultDrawingColor('polyline');
  }
  if (drawing.type === 'polygon') {
    return (drawing.layer as L.Polygon).options.color || defaultDrawingColor('polygon');
  }
  return defaultDrawingColor('marker');
}

interface DrawingItem {
  id: string;
  type: DrawingMode;
  layer: L.Layer;
  label?: string;
  notes?: string;
  color?: string;
  customIcon?: string;
  locked?: boolean;
}

// Serializable version for server sync (without Leaflet layer)
type SerializedDrawing = EventMapDrawing;

interface MapDrawingToolsProps {
  className?: string;
  incidents?: Incident[];
  onIncidentClick?: (incident: Incident) => void;
  isPanel?: boolean;
  onClose?: () => void;
  center?: [number, number];
  zoom?: number;
  /** When true (pop-out map), fit view to incidents + drawings once loaded. */
  autoFitContent?: boolean;
}

export default function MapDrawingTools({
  className,
  incidents = [],
  onIncidentClick,
  isPanel = false,
  onClose,
  center,
  zoom,
  autoFitContent = false,
}: MapDrawingToolsProps) {
  const { token } = useAppSelector((state) => state.auth);
  const emergencyLocation = useAppSelector((state) => state.ui.emergencyLocation);
  const { activeEventId, activeEvent, loading: eventLoading } = useActiveEvent();
  const eventMapCenter = useMemo(() => getEventMapCenter(activeEvent), [activeEvent]);
  const centerLat = center?.[0];
  const centerLng = center?.[1];
  const incidentsMarkerKey = useMemo(
    () =>
      incidents
        .filter((inc) => inc.status !== 'closed')
        .map(
          (inc) =>
            `${inc.id}:${inc.location?.latitude}:${inc.location?.longitude}:${inc.severity}:${inc.title}`
        )
        .join('|'),
    [incidents]
  );
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<L.Map | null>(null);
  const markersRef = useRef<L.LayerGroup>(L.layerGroup());
  const trackingRef = useRef<L.LayerGroup>(L.layerGroup());
  const drawnItemsRef = useRef<L.FeatureGroup>(new L.FeatureGroup());
  const emergencyMarkerRef = useRef<L.Marker | null>(null);
  const emergencyFlownIncidentRef = useRef<string | null>(null);
  const overlayLayerRef = useRef<RotatedOverlayLayer | null>(null);
  const mapEventIdRef = useRef<string | null>(null);
  const leafletStyleRef = useRef<HTMLStyleElement | null>(null);

  // Deserialize drawing from localStorage and create Leaflet layer
  const deserializeDrawing = (serialized: SerializedDrawing): DrawingItem | null => {
    let layer: L.Layer | null = null;

    try {
      if (serialized.type === 'marker' && serialized.latlng) {
        layer = L.marker(serialized.latlng as L.LatLngExpression, {
          icon: createColoredMarkerIcon(serialized.color || defaultDrawingColor('marker')),
        });
      } else if (serialized.type === 'circle' && serialized.latlng && serialized.radius) {
        layer = L.circle(serialized.latlng as L.LatLngExpression, {
          radius: serialized.radius,
          color: serialized.color || '#2563eb',
          fillColor: serialized.color || '#2563eb',
          fillOpacity: 0.2,
        });
      } else if (serialized.type === 'polyline' && serialized.latlngs) {
        layer = L.polyline(serialized.latlngs as L.LatLngExpression[], {
          color: serialized.color || '#f97316',
        });
      } else if (serialized.type === 'polygon' && serialized.latlngs) {
        layer = L.polygon(serialized.latlngs as L.LatLngExpression[][], {
          color: serialized.color || '#7c3aed',
          fillColor: serialized.color || '#7c3aed',
          fillOpacity: 0.2,
        });
      }

      if (layer) {
        return {
          id: serialized.id,
          type: serialized.type,
          layer,
          label: serialized.label,
          notes: serialized.notes,
          color: serialized.color,
          customIcon: serialized.customIcon,
          // Loaded drawings were previously saved, so default to locked
          locked: serialized.locked !== false,
        };
      }
    } catch (error) {
      console.error('Error deserializing drawing:', error);
    }

    return null;
  };

  const [activeMode, setActiveMode] = useState<DrawingMode>('none');
  const [drawings, setDrawings] = useState<DrawingItem[]>([]);
  const drawingsRef = useRef(drawings);
  drawingsRef.current = drawings;
  const [circleRadius, setCircleRadius] = useState(500);
  const [mapReady, setMapReady] = useState(false);
  const [showOverlayPanel, setShowOverlayPanel] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [showDrawingList, setShowDrawingList] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    incidents: true,
    drawings: true,
    tracking: false,
  });
  const [lastClickPoint, setLastClickPoint] = useState<L.LatLng | null>(null);
  const [overlappingDrawings, setOverlappingDrawings] = useState<DrawingItem[]>([]);
  const [currentOverlapIndex, setCurrentOverlapIndex] = useState(0);
  const drawHandlerRef = useRef<L.Draw.Marker | L.Draw.Circle | L.Draw.Polyline | L.Draw.Polygon | null>(null);

  const destroyMap = useCallback(() => {
    if (mapRef.current) {
      try {
        mapRef.current.remove();
      } catch {
        // already disposed
      }
      mapRef.current = null;
    }
    if (containerRef.current) {
      delete (containerRef.current as HTMLDivElement & { _leaflet_id?: number })._leaflet_id;
    }
    overlayLayerRef.current = null;
    mapEventIdRef.current = null;
    setMapReady(false);
  }, []);

  const overlaySaveTimerRef = useRef<number | null>(null);
  const drawingsSaveTimerRef = useRef<number | null>(null);
  const serverDrawingsJsonRef = useRef<string>('[]');
  const persistDrawingsRef = useRef<(items: DrawingItem[]) => Promise<void>>(async () => {});
  const {
    overlay: mapOverlay,
    saving: overlaySaving,
    saveOverlay,
    removeOverlay,
    setOverlayLocal,
  } = useEventMapOverlay({ eventId: activeEventId, token });
  const {
    drawings: serverDrawings,
    saveDrawings,
    clearDrawings,
  } = useEventMapDrawings({ eventId: activeEventId, token });

  // Apply server drawings when they change (shared across all users).
  useEffect(() => {
    if (!mapReady) return;

    const json = JSON.stringify(serverDrawings);
    if (json === serverDrawingsJsonRef.current) return;
    serverDrawingsJsonRef.current = json;

    drawnItemsRef.current.clearLayers();
    const deserialized = serverDrawings
      .map((serialized) => deserializeDrawing(serialized))
      .filter((d): d is DrawingItem => d !== null);

    deserialized.forEach((drawing) => {
      drawnItemsRef.current.addLayer(drawing.layer);
      drawing.layer.bindPopup(
        () => createEditPopupForDrawing(drawing.id, drawing.layer, drawing.type),
        { maxWidth: 300 }
      );
      (drawing.layer as any)._drawingId = drawing.id;
      (drawing.layer as any)._drawingType = drawing.type;
    });

    setDrawings(deserialized);
    mapRef.current?.invalidateSize();
  }, [serverDrawings, mapReady]);

  const handleOverlayChange = useCallback(
    (next: EventMapOverlaySavePayload | null) => {
      if (!next) {
        void removeOverlay();
        return;
      }

      const center = eventMapCenter;
      const payload: EventMapOverlaySavePayload = {
        ...next,
        centerLat: next.centerLat || center?.[0] || 0,
        centerLng: next.centerLng || center?.[1] || 0,
      };

      if (mapOverlay?.imageUrl) {
        setOverlayLocal({ ...payload, imageUrl: mapOverlay.imageUrl });
      }

      if (payload.imageDataUrl) {
        void saveOverlay(payload);
        return;
      }

      if (overlaySaveTimerRef.current) window.clearTimeout(overlaySaveTimerRef.current);
      overlaySaveTimerRef.current = window.setTimeout(() => {
        void saveOverlay(payload);
      }, 450);
    },
    [eventMapCenter, mapOverlay?.imageUrl, removeOverlay, saveOverlay, setOverlayLocal]
  );

  // Render / update overlay on map
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady) return;

    if (!mapOverlay) {
      if (overlayLayerRef.current) {
        map.removeLayer(overlayLayerRef.current);
        overlayLayerRef.current = null;
      }
      return;
    }

    const center = eventMapCenter || [mapOverlay.centerLat, mapOverlay.centerLng];
    const config = {
      ...mapOverlay,
      centerLat: mapOverlay.centerLat || center[0],
      centerLng: mapOverlay.centerLng || center[1],
    };

    if (overlayLayerRef.current) {
      updateOverlayLayer(overlayLayerRef.current, config);
    } else {
      overlayLayerRef.current = createOverlayLayer(map, config);
    }

    const onMapChange = () => overlayLayerRef.current?.bringToFront();
    map.on('moveend zoomend resize', onMapChange);
    return () => {
      map.off('moveend zoomend resize', onMapChange);
    };
  }, [mapOverlay, mapReady, eventMapCenter]);

  // Keep site map above vector layers when drawings refresh
  useEffect(() => {
    overlayLayerRef.current?.bringToFront();
  }, [serverDrawings, mapReady]);

  // Serialize drawing to save to server (layer style is source of truth for color).
  const serializeDrawing = (drawing: DrawingItem): SerializedDrawing => {
    const color = readDrawingColor(drawing);
    const serialized: SerializedDrawing = {
      id: drawing.id,
      type: drawing.type as SerializedDrawing['type'],
      label: drawing.label,
      notes: drawing.notes,
      color,
      customIcon: drawing.customIcon,
      locked: drawing.locked,
    };

    if (drawing.type === 'marker') {
      const marker = drawing.layer as L.Marker;
      const ll = marker.getLatLng();
      serialized.latlng = { lat: ll.lat, lng: ll.lng };
    } else if (drawing.type === 'circle') {
      const circle = drawing.layer as L.Circle;
      const ll = circle.getLatLng();
      serialized.latlng = { lat: ll.lat, lng: ll.lng };
      serialized.radius = circle.getRadius();
    } else if (drawing.type === 'polyline') {
      const polyline = drawing.layer as L.Polyline;
      const latlngs = polyline.getLatLngs() as L.LatLng[];
      serialized.latlngs = latlngs.map((ll) => ({ lat: ll.lat, lng: ll.lng }));
    } else if (drawing.type === 'polygon') {
      const polygon = drawing.layer as L.Polygon;
      const rings = polygon.getLatLngs() as L.LatLng[][];
      serialized.latlngs = rings.map((ring) =>
        ring.map((ll) => ({ lat: ll.lat, lng: ll.lng }))
      );
    }

    return serialized;
  };

  const persistDrawings = useCallback(
    async (items: DrawingItem[]) => {
      if (!activeEventId || !token) return;

      const serialized = items.map(serializeDrawing);
      const json = JSON.stringify(serialized);
      if (drawingsSaveTimerRef.current) {
        window.clearTimeout(drawingsSaveTimerRef.current);
        drawingsSaveTimerRef.current = null;
      }

      // Mark as synced before the hook updates serverDrawings (prevents apply effect closing popups).
      serverDrawingsJsonRef.current = json;

      try {
        const saved = await saveDrawings(serialized);
        if (saved) {
          serverDrawingsJsonRef.current = JSON.stringify(saved);
        }
      } catch {
        serverDrawingsJsonRef.current = JSON.stringify(serverDrawings);
      }
    },
    [activeEventId, token, saveDrawings, serverDrawings]
  );

  persistDrawingsRef.current = persistDrawings;

  // Helper function to check if point is inside polygon
  const isPointInPolygon = (point: L.LatLng, polygon: L.LatLng[]): boolean => {
    let inside = false;
    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
      const xi = polygon[i].lat, yi = polygon[i].lng;
      const xj = polygon[j].lat, yj = polygon[j].lng;
      const intersect = ((yi > point.lng) !== (yj > point.lng))
        && (point.lat < (xj - xi) * (point.lng - yi) / (yj - yi) + xi);
      if (intersect) inside = !inside;
    }
    return inside;
  };

  // Add newly created drawings to the map layer group (server sync handles persistence).
  useEffect(() => {
    if (!mapReady) return;

    drawings.forEach((drawing) => {
      if (!drawnItemsRef.current.hasLayer(drawing.layer)) {
        drawnItemsRef.current.addLayer(drawing.layer);
        drawing.layer.bindPopup(
          () => createEditPopupForDrawing(drawing.id, drawing.layer, drawing.type),
          { maxWidth: 300 }
        );
        (drawing.layer as any)._drawingId = drawing.id;
        (drawing.layer as any)._drawingType = drawing.type;
      }
    });
  }, [drawings, mapReady]);

  // Helper function to create popup for a drawing - locked = read-only view, unlocked = edit form
  const createEditPopupForDrawing = (drawingId: string, layer: L.Layer, type: DrawingMode) => {
    const drawing = drawingsRef.current.find(d => d.id === drawingId);
    if (!drawing) {
      console.warn('Drawing not found:', drawingId);
      return document.createElement('div');
    }

    const container = document.createElement('div');
    container.className = 'map-drawing-popup';
    container.style.cssText = 'background: hsl(var(--card)); border: 1px solid hsl(var(--border)); border-radius: 6px; box-shadow: 0 4px 12px rgba(0,0,0,0.15); width: 200px;';
    L.DomEvent.disableClickPropagation(container);
    L.DomEvent.disableScrollPropagation(container);

    // Pending edits — kept local until Save (avoids server sync closing the popup).
    let pendingColor = readDrawingColor({ ...drawing, type, layer });

    // Build either the locked (read-only) view or the edit form
    const renderContent = (isLocked: boolean) => {
      const current = drawingsRef.current.find(d => d.id === drawingId) || drawing;
      if (!isLocked) {
        pendingColor = readDrawingColor({ ...current, type, layer });
      }
      const currentColor = isLocked
        ? readDrawingColor({ ...current, type, layer })
        : pendingColor;

      if (isLocked) {
        // READ-ONLY VIEW
        let details = '';
        if (type === 'circle') {
          const r = Math.round((layer as L.Circle).getRadius());
          details = `<div class="text-[10px] text-muted-foreground">Radius: ${r}m</div>`;
        } else if (type === 'marker') {
          const ll = (layer as L.Marker).getLatLng();
          details = `<div class="text-[10px] text-muted-foreground font-mono">${ll.lat.toFixed(5)}, ${ll.lng.toFixed(5)}</div>`;
        }
        container.innerHTML = `
          <div class="p-2 pr-5">
            <div class="font-bold text-[12px] text-foreground break-words mb-1">${current.label || type}</div>
            ${current.notes ? `<div class="text-[11px] text-muted-foreground mb-1.5 break-words">${current.notes}</div>` : ''}
            ${details}
            <div class="mt-2 pt-1.5 border-t border-border flex">
              <button id="unlock-${drawingId}" title="Edit" class="flex-1 flex items-center justify-center gap-1 px-2 py-1 text-[11px] font-medium rounded bg-accent/50 text-foreground hover:bg-accent transition-colors">
                <svg xmlns="http://www.w3.org/2000/svg" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z"/><path d="m15 5 4 4"/></svg>
                Edit
              </button>
            </div>
          </div>
        `;
        setTimeout(() => {
          const unlockBtn = container.querySelector(`#unlock-${drawingId}`);
          unlockBtn?.addEventListener('click', (e) => {
            L.DomEvent.stopPropagation(e);
            renderContent(false);
          });
        }, 10);
        return;
      }

      // EDIT FORM
      let extraControls = '';
      if (type === 'circle') {
        const r = Math.round((layer as L.Circle).getRadius());
        extraControls = `
          <div class="mb-1.5">
            <label class="text-[9px] text-muted-foreground block mb-0.5">Radius (m)</label>
            <input type="number" id="radius-${drawingId}" value="${r}" min="10" step="10"
              class="w-full px-1.5 py-0.5 text-[11px] rounded border border-border bg-background text-foreground" />
          </div>
        `;
      }

      container.innerHTML = `
        <div class="p-2">
          <div class="mb-1.5">
            <label class="text-[9px] text-muted-foreground block mb-0.5">Label</label>
            <input type="text" id="label-${drawingId}" value="${current.label || type}"
              class="w-full px-1.5 py-0.5 text-[11px] rounded border border-border bg-background text-foreground font-medium" />
          </div>
          <div class="mb-1.5">
            <label class="text-[9px] text-muted-foreground block mb-0.5">Notes</label>
            <textarea id="notes-${drawingId}" rows="2" placeholder="Add notes..."
              class="w-full px-1.5 py-0.5 text-[11px] rounded border border-border bg-background text-foreground resize-none">${current.notes || ''}</textarea>
          </div>
          ${extraControls}
          <div class="mb-1.5">
            <label class="text-[9px] text-muted-foreground block mb-0.5">Color</label>
            <div class="flex gap-0.5 flex-wrap">
              ${['#2563eb', '#f97316', '#7c3aed', '#22c55e', '#ef4444', '#eab308', '#06b6d4', '#ec4899'].map(color => `
                <button type="button" class="color-btn w-4 h-4 rounded border-2 ${color === currentColor ? 'border-foreground' : 'border-transparent'} hover:border-muted-foreground transition-colors"
                  style="background: ${color};" data-color="${color}"></button>
              `).join('')}
            </div>
          </div>
          <div class="flex gap-1 mt-2 pt-1.5 border-t border-border">
            <button type="button" id="save-${drawingId}" class="flex-1 px-2 py-1 text-[11px] font-medium rounded bg-primary text-primary-foreground hover:bg-primary/90 transition-colors">
              Save
            </button>
            <button type="button" id="delete-${drawingId}" class="px-2 py-1 text-[11px] font-medium rounded bg-destructive/10 text-destructive hover:bg-destructive/20 transition-colors">
              Delete
            </button>
          </div>
        </div>
      `;

      setTimeout(() => {
        container.querySelectorAll('.color-btn').forEach(btn => {
          btn.addEventListener('click', (e) => {
            L.DomEvent.stopPropagation(e);
            e.preventDefault();
            const target = e.currentTarget as HTMLElement;
            const newColor = target.getAttribute('data-color');
            if (!newColor) return;

            pendingColor = newColor;
            container.querySelectorAll('.color-btn').forEach(b => b.classList.remove('border-foreground'));
            target.classList.add('border-foreground');

            applyDrawingColor(layer, type, newColor);
          });
        });

        const radiusInput = container.querySelector(`#radius-${drawingId}`) as HTMLInputElement;
        if (radiusInput && type === 'circle') {
          radiusInput.addEventListener('input', (e) => {
            const newRadius = parseInt((e.target as HTMLInputElement).value);
            if (newRadius > 0) {
              (layer as L.Circle).setRadius(newRadius);
            }
          });
        }

        const saveBtn = container.querySelector(`#save-${drawingId}`);
        saveBtn?.addEventListener('click', (e) => {
          L.DomEvent.stopPropagation(e);
          e.preventDefault();
          const labelInput = container.querySelector(`#label-${drawingId}`) as HTMLInputElement;
          const notesInput = container.querySelector(`#notes-${drawingId}`) as HTMLTextAreaElement;

          const nextItems = drawingsRef.current.map(item => {
            if (item.id !== drawingId) return item;
            applyDrawingColor(item.layer, type, pendingColor);
            return {
              ...item,
              label: labelInput?.value || type,
              notes: notesInput?.value || '',
              color: pendingColor,
              locked: true,
            };
          });

          setDrawings(nextItems);
          void persistDrawingsRef.current(nextItems);

          renderContent(true);
          (layer as any).getPopup?.()?.update?.();
        });

        const deleteBtn = container.querySelector(`#delete-${drawingId}`);
        deleteBtn?.addEventListener('click', (e) => {
          L.DomEvent.stopPropagation(e);
          e.preventDefault();
          removeDrawing(drawingId);
          layer.closePopup();
        });
      }, 10);
    };

    renderContent(drawing.locked === true);

    return container;
  };

  // Initialize map once event context is ready.
  useEffect(() => {
    if (!containerRef.current) return;
    if (activeEventId && eventLoading) return;

    const scopedEventId = activeEventId ?? null;
    if (mapRef.current && mapEventIdRef.current === scopedEventId) return;

    if (mapRef.current) {
      destroyMap();
    }

    if (!leafletStyleRef.current) {
      const style = document.createElement('style');
      style.textContent = `
        .leaflet-tooltip { z-index: 50 !important; }
        .leaflet-popup { z-index: 50 !important; }
        .leaflet-control-container { z-index: 5 !important; }
      `;
      document.head.appendChild(style);
      leafletStyleRef.current = style;
    }

    const initialCenter: [number, number] =
      centerLat != null && centerLng != null
        ? [centerLat, centerLng]
        : eventMapCenter || [51.505, -0.09];
    const initialZoom = zoom ?? (centerLat != null && centerLng != null || eventMapCenter ? 15 : 13);

    const map = L.map(containerRef.current, {
      center: initialCenter,
      zoom: initialZoom,
      zoomControl: true,
      preferCanvas: true,
    });

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
      maxZoom: 19,
      keepBuffer: 2,
      updateWhenIdle: true,
      updateWhenZooming: false,
    }).addTo(map);

    markersRef.current.addTo(map);
    trackingRef.current.addTo(map);
    drawnItemsRef.current.addTo(map);
    mapRef.current = map;
    mapEventIdRef.current = scopedEventId;
    setMapReady(true);

    map.on('click', (e: L.LeafletMouseEvent) => {
      const clickPoint = e.latlng;
      const tolerance = 20;
      const point = map.latLngToContainerPoint(clickPoint);
      const currentDrawings = drawingsRef.current;

      const drawingsAtPoint = currentDrawings.filter((drawing) => {
        try {
          if (drawing.type === 'marker') {
            const marker = drawing.layer as L.Marker;
            const markerPoint = map.latLngToContainerPoint(marker.getLatLng());
            const distance = Math.sqrt(
              Math.pow(point.x - markerPoint.x, 2) + Math.pow(point.y - markerPoint.y, 2)
            );
            return distance <= tolerance;
          } else if (drawing.type === 'circle') {
            const circle = drawing.layer as L.Circle;
            return map.distance(clickPoint, circle.getLatLng()) <= circle.getRadius();
          } else if (drawing.type === 'polyline') {
            return (drawing.layer as L.Polyline).getBounds().contains(clickPoint);
          } else if (drawing.type === 'polygon') {
            const polygon = drawing.layer as L.Polygon;
            if (!polygon.getBounds().contains(clickPoint)) return false;
            const latlngs = polygon.getLatLngs()[0] as L.LatLng[];
            return isPointInPolygon(clickPoint, latlngs);
          }
        } catch (error) {
          console.error('Error checking drawing bounds:', error);
        }
        return false;
      });

      if (drawingsAtPoint.length > 0) {
        const isSameLocation =
          lastClickPoint && map.distance(lastClickPoint, clickPoint) < 10;

        if (isSameLocation && drawingsAtPoint.length > 1) {
          const nextIndex = (currentOverlapIndex + 1) % drawingsAtPoint.length;
          setCurrentOverlapIndex(nextIndex);
          drawingsAtPoint[nextIndex].layer.openPopup();
        } else {
          setLastClickPoint(clickPoint);
          setOverlappingDrawings(drawingsAtPoint);
          setCurrentOverlapIndex(0);
          drawingsAtPoint[0].layer.openPopup();
        }
      } else {
        setLastClickPoint(null);
        setOverlappingDrawings([]);
        setCurrentOverlapIndex(0);
      }
    });

    const invalidateTimer = window.setTimeout(() => {
      if (mapRef.current === map) map.invalidateSize();
    }, 150);

    return () => {
      window.clearTimeout(invalidateTimer);
      destroyMap();
    };
  }, [activeEventId, eventLoading, destroyMap]);

  // Centre map when event location or fixed centre props arrive after init.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady || eventLoading) return;

    if (centerLat != null && centerLng != null) {
      map.setView([centerLat, centerLng], zoom ?? map.getZoom());
      return;
    }

    if (eventMapCenter) {
      map.setView(eventMapCenter, zoom ?? 15);
    }
  }, [centerLat, centerLng, eventMapCenter, zoom, mapReady, eventLoading]);

  useEffect(() => {
    if (!mapReady || !mapRef.current) return;
    const map = mapRef.current;
    const timers = [150, 400, 900].map((ms) =>
      window.setTimeout(() => map.invalidateSize(), ms)
    );
    return () => timers.forEach((id) => window.clearTimeout(id));
  }, [mapReady]);

  // Pop-out: fit map to all visible content once data has loaded.
  useEffect(() => {
    if (!autoFitContent || !mapReady || !mapRef.current) return;
    if (activeEventId && eventLoading) return;

    const map = mapRef.current;
    const bounds = L.latLngBounds([]);

    if (eventMapCenter) {
      bounds.extend(eventMapCenter);
    }

    incidents.forEach((inc) => {
      if (inc.location?.latitude && inc.location?.longitude) {
        bounds.extend([inc.location.latitude, inc.location.longitude]);
      }
    });

    serverDrawings.forEach((drawing) => {
      if (drawing.latlng) {
        bounds.extend([drawing.latlng.lat, drawing.latlng.lng]);
      }
      if (drawing.latlngs && Array.isArray(drawing.latlngs)) {
        const flat = Array.isArray(drawing.latlngs[0])
          ? (drawing.latlngs as Array<Array<{ lat: number; lng: number }>>).flat()
          : (drawing.latlngs as Array<{ lat: number; lng: number }>);
        flat.forEach((pt) => {
          if (pt && typeof pt.lat === 'number' && typeof pt.lng === 'number') {
            bounds.extend([pt.lat, pt.lng]);
          }
        });
      }
    });

    if (mapOverlay) {
      const corners = computeOverlayCorners(
        mapOverlay.centerLat,
        mapOverlay.centerLng,
        mapOverlay.widthMeters,
        mapOverlay.heightMeters,
        mapOverlay.rotation
      );
      bounds.extend(corners.topLeft);
      bounds.extend(corners.topRight);
      bounds.extend(corners.bottomLeft);
    }

    if (bounds.isValid()) {
      map.fitBounds(bounds, { padding: [48, 48], maxZoom: 17 });
    } else if (eventMapCenter) {
      map.setView(eventMapCenter, zoom ?? 15);
    }
  }, [
    autoFitContent,
    mapReady,
    activeEventId,
    eventLoading,
    incidents,
    serverDrawings,
    mapOverlay,
    eventMapCenter,
    zoom,
  ]);

  // Update incident markers
  useEffect(() => {
    const group = markersRef.current;
    group.clearLayers();

    incidents
      .filter((inc) => inc.status !== 'closed') // Exclude closed incidents
      .forEach((inc) => {
        if (!inc.location?.latitude || !inc.location?.longitude) return;
        const marker = L.marker(
          [inc.location.latitude, inc.location.longitude],
          { icon: createIncidentIcon(inc.severity) }
        );
        marker.bindTooltip(
          `<strong>${inc.id}</strong><br/>${inc.title}<br/><em>${inc.severity.toUpperCase()}</em>`,
          { className: 'map-tooltip', direction: 'top', offset: [0, -8] }
        );
        if (onIncidentClick) {
        marker.on('click', () => onIncidentClick(inc));
      }
      group.addLayer(marker);
    });
  }, [incidentsMarkerKey, onIncidentClick]);

  // MDT tracking layer: fetch initial + listen for socket updates
  useEffect(() => {
    const trackingGroup = trackingRef.current;

    const statusColors: Record<string, string> = {
      available: '#22c55e', busy: '#f59e0b', enroute: '#06b6d4',
      onscene: '#ef4444', onbreak: '#a855f7',
    };

    function createTrackingIcon(status: string) {
      const color = statusColors[status] || '#3b82f6';
      return L.divIcon({
        className: 'mdt-tracking-marker',
        html: `<div style="width:20px;height:20px;border-radius:50%;background:${color};border:3px solid #fff;box-shadow:0 0 10px ${color}80,0 0 20px ${color}40;display:flex;align-items:center;justify-content:center;"><div style="width:6px;height:6px;background:#fff;border-radius:50%;"></div></div>`,
        iconSize: [20, 20],
        iconAnchor: [10, 10],
      });
    }

    function renderTracking(positions: any[]) {
      trackingGroup.clearLayers();
      positions.forEach((pos: any) => {
        if (!pos.latitude || !pos.longitude) return;
        const marker = L.marker(
          [pos.latitude, pos.longitude],
          { icon: createTrackingIcon(pos.status) }
        );
        const ago = pos.timestamp ? new Date(pos.timestamp).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false }) : '';
        marker.bindTooltip(
          `<strong>${pos.resourceName}</strong><br/>${pos.userName}<br/><em>${(pos.status || 'unknown').replace(/([a-z])([A-Z])/g, '$1 $2')}</em><br/><span style="font-size:10px;opacity:0.6">${ago}</span>`,
          { className: 'map-tooltip', direction: 'top', offset: [0, -12], permanent: false }
        );
        trackingGroup.addLayer(marker);
      });
    }

    if (!token) return;

    const fetchTracking = () => {
      fetch(`${API_BASE_URL}/tracking`, { headers: { Authorization: `Bearer ${token}` } })
        .then((r) => r.json())
        .then((d) => {
          renderTracking(d.positions || []);
        })
        .catch((err) => console.error('Tracking fetch error:', err));
    };

    fetchTracking();
    const interval = setInterval(fetchTracking, 3000);

    const socket: Socket = io(SOCKET_URL, {
      auth: { token },
      transports: ['websocket', 'polling'],
    });
    socket.on('mdt:tracking_update', (positions: unknown) => {
      renderTracking(Array.isArray(positions) ? positions : []);
    });

    return () => {
      clearInterval(interval);
      socket.disconnect();
    };
  }, [token]);

  // Resize handler
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const observer = new ResizeObserver(() => map.invalidateSize());
    if (containerRef.current) observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  // Action bar ZTR — zoom map to resource tracking position
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<MapZoomResourceDetail>).detail;
      const map = mapRef.current;
      if (!map || !mapReady || detail.offline) return;
      if (detail.latitude != null && detail.longitude != null) {
        map.flyTo([detail.latitude, detail.longitude], 17, { duration: 1.2 });
      }
    };
    window.addEventListener(MAP_ZOOM_RESOURCE_EVENT, handler);
    return () => window.removeEventListener(MAP_ZOOM_RESOURCE_EVENT, handler);
  }, [mapReady]);

  // Show emergency marker for the active event only; fly once per incident activation.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    if (emergencyMarkerRef.current) {
      map.removeLayer(emergencyMarkerRef.current);
      emergencyMarkerRef.current = null;
    }

    if (!emergencyMatchesEvent(emergencyLocation, activeEventId)) {
      if (activeEventId && emergencyFlownIncidentRef.current) {
        emergencyFlownIncidentRef.current = null;
      }
      return;
    }

    const emergencyIcon = L.divIcon({
      className: 'emergency-marker',
      html: `<div style="width:30px;height:30px;border-radius:50%;background:#ef4444;border:4px solid #fff;box-shadow:0 0 20px #ef4444;animation:pulse 1s infinite;"></div>`,
      iconSize: [30, 30],
      iconAnchor: [15, 15],
    });

    const marker = L.marker([emergencyLocation.latitude, emergencyLocation.longitude], {
      icon: emergencyIcon,
    });

    marker.bindTooltip('EMERGENCY LOCATION', {
      className: 'emergency-tooltip',
      direction: 'top',
      offset: [0, -20],
      permanent: true,
    });

    marker.addTo(map);
    emergencyMarkerRef.current = marker;

    if (emergencyFlownIncidentRef.current !== emergencyLocation.incidentId) {
      map.flyTo([emergencyLocation.latitude, emergencyLocation.longitude], 19, {
        duration: 1.5,
      });
      emergencyFlownIncidentRef.current = emergencyLocation.incidentId;
    }
  }, [emergencyLocation, activeEventId]);

  // Re-centre on event when switching events (without stale emergency hijacking the view).
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady || !activeEventId) return;
    if (emergencyMatchesEvent(emergencyLocation, activeEventId)) return;

    const centre = eventMapCenter;
    if (centre) {
      map.setView(centre, map.getZoom());
    }
  }, [activeEventId, eventMapCenter, mapReady, emergencyLocation]);
  const cancelCurrentDraw = () => {
    if (drawHandlerRef.current) {
      try {
        (drawHandlerRef.current as unknown as { disable: () => void }).disable();
      } catch { /* ignore */ }
      drawHandlerRef.current = null;
    }
    setActiveMode('none');
  };

  const startDrawing = (mode: DrawingMode) => {
    const map = mapRef.current;
    if (!map) return;

    cancelCurrentDraw();

    if (mode === activeMode) {
      setActiveMode('none');
      return;
    }

    setActiveMode(mode);

    let handler: L.Draw.Marker | L.Draw.Circle | L.Draw.Polyline | L.Draw.Polygon | null = null;

    switch (mode) {
      case 'marker':
        handler = new L.Draw.Marker(map as any, {
          icon: L.icon({
            iconUrl: 'https://unpkg.com/leaflet@1.9.3/dist/images/marker-icon.png',
            iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.3/dist/images/marker-icon-2x.png',
            shadowUrl: 'https://unpkg.com/leaflet@1.9.3/dist/images/marker-shadow.png',
            iconSize: [25, 41],
            iconAnchor: [12, 41],
          }),
        });
        break;
      case 'circle':
        handler = new L.Draw.Circle(map as any, {
          shapeOptions: {
            color: '#2563eb',
            fillColor: '#2563eb',
            fillOpacity: 0.15,
            weight: 2,
          },
        });
        break;
      case 'polyline':
        handler = new L.Draw.Polyline(map as any, {
          shapeOptions: {
            color: '#f97316',
            weight: 3,
            opacity: 0.8,
          },
        });
        break;
      case 'polygon':
        handler = new L.Draw.Polygon(map as any, {
          shapeOptions: {
            color: '#7c3aed',
            fillColor: '#7c3aed',
            fillOpacity: 0.15,
            weight: 2,
          },
        });
        break;
    }

    if (handler) {
      handler.enable();
      drawHandlerRef.current = handler;
    }
  };

  // Listen for draw:created events
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    const onCreated = (e: L.LeafletEvent) => {
      const event = e as L.DrawEvents.Created;
      const layer = event.layer;
      drawnItemsRef.current.addLayer(layer);

      const id = `draw-${Date.now()}`;
      const type = event.layerType as DrawingMode;

      // Bind popup that will be created on demand
      layer.bindPopup(() => createEditPopupForDrawing(id, layer, type), { maxWidth: 300 });
      
      // Store drawing ID on the layer for later reference
      (layer as any)._drawingId = id;
      (layer as any)._drawingType = type;

      // Set initial drawing data with default color
      const defaultColor = defaultDrawingColor(type);
      const newItem: DrawingItem = { id, type, layer, label: type, color: defaultColor, locked: false };
      const nextItems = [...drawingsRef.current, newItem];
      setDrawings(nextItems);
      applyDrawingColor(layer, type, defaultColor);
      void persistDrawingsRef.current(nextItems);
      setActiveMode('none');
      drawHandlerRef.current = null;
    };

    map.on(L.Draw.Event.CREATED, onCreated);
    return () => {
      map.off(L.Draw.Event.CREATED, onCreated);
    };
  }, []);

  const removeDrawing = (id: string) => {
    const nextItems = drawingsRef.current.filter((d) => {
      if (d.id === id) {
        drawnItemsRef.current.removeLayer(d.layer);
        return false;
      }
      return true;
    });
    setDrawings(nextItems);
    void persistDrawingsRef.current(nextItems);
  };

  const clearAll = () => {
    drawnItemsRef.current.clearLayers();
    setDrawings([]);
    serverDrawingsJsonRef.current = '[]';
    cancelCurrentDraw();
    void clearDrawings();
  };

  const toolButtons = [
    { mode: 'marker' as DrawingMode, icon: MapPin, label: 'Marker', color: 'text-blue-400' },
    { mode: 'circle' as DrawingMode, icon: Circle, label: 'Circle Radius', color: 'text-blue-400' },
    { mode: 'polyline' as DrawingMode, icon: Navigation, label: 'Route / Polyline', color: 'text-orange-400' },
    { mode: 'polygon' as DrawingMode, icon: Hexagon, label: 'Zone / Polygon', color: 'text-purple-400' },
  ];

  if (isPanel && isMinimized) {
    return (
      <div className="flex items-center justify-between px-3 py-2 bg-card border-t border-border">
        <span className="text-xs font-semibold text-foreground">Map Panel</span>
        <div className="flex items-center gap-1">
          <button onClick={() => setIsMinimized(false)} className="p-1 hover:bg-accent rounded transition-colors">
            <Maximize2 className="w-3 h-3 text-muted-foreground" />
          </button>
          {onClose && (
            <button onClick={onClose} className="p-1 hover:bg-destructive/10 rounded transition-colors">
              <X className="w-3 h-3 text-muted-foreground" />
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={cn('flex flex-col', className)}>
      {/* Toolbar */}
      <div className="flex items-center gap-1 px-2 py-1.5 bg-card border-b border-border flex-shrink-0">
        {toolButtons.map((btn) => (
          <button
            key={btn.mode}
            onClick={() => startDrawing(btn.mode)}
            className={cn(
              'flex items-center gap-1 px-2 py-1 rounded text-[10px] font-semibold transition-colors',
              activeMode === btn.mode
                ? 'bg-primary text-primary-foreground'
                : 'text-muted-foreground hover:bg-accent hover:text-foreground'
            )}
            title={btn.label}
          >
            <btn.icon className="w-3 h-3" />
            <span className="hidden lg:inline">{btn.label}</span>
          </button>
        ))}

        <div className="w-px h-4 bg-border mx-1" />

        {/* Circle radius slider when circle mode active */}
        {activeMode === 'circle' && (
          <div className="flex items-center gap-1.5 text-[10px]">
            <span className="text-muted-foreground">R:</span>
            <input
              type="range"
              min={50}
              max={5000}
              step={50}
              value={circleRadius}
              onChange={(e) => setCircleRadius(Number(e.target.value))}
              className="w-20 h-1 accent-primary"
            />
            <span className="text-foreground font-mono w-10">{circleRadius}m</span>
          </div>
        )}

        <button
          onClick={() => {
            setShowOverlayPanel(!showOverlayPanel);
            if (!showOverlayPanel) setShowDrawingList(false);
          }}
          className={cn(
            'flex items-center gap-1 px-2 py-1 rounded text-[10px] font-semibold transition-colors',
            showOverlayPanel || mapOverlay
              ? 'bg-primary/15 text-primary'
              : 'text-muted-foreground hover:bg-accent'
          )}
          title="Upload & align event site plan overlay"
        >
          <ImageIcon className="w-3 h-3" />
          Site map
        </button>

        <button
          onClick={() => {
            setShowDrawingList(!showDrawingList);
            if (!showDrawingList) setShowOverlayPanel(false);
          }}
          className={cn(
            'flex items-center gap-1 px-2 py-1 rounded text-[10px] font-semibold transition-colors',
            showDrawingList ? 'bg-accent text-foreground' : 'text-muted-foreground hover:bg-accent'
          )}
          title="Map Key / Legend"
        >
          <Layers className="w-3 h-3" />
          Key
        </button>

        <button
          onClick={clearAll}
          disabled={drawings.length === 0}
          className="flex items-center gap-1 px-2 py-1 rounded text-[10px] font-semibold text-muted-foreground hover:bg-destructive/10 hover:text-destructive transition-colors disabled:opacity-30"
          title="Clear all drawings"
        >
          <Trash2 className="w-3 h-3" />
        </button>

        {isPanel && (
          <div className="ml-auto flex items-center gap-1">
            <button 
              onClick={() => {
                const url = activeEventId
                  ? `/workspace/map?eventId=${encodeURIComponent(activeEventId)}`
                  : '/workspace/map';
                window.open(url, '_blank');
              }}
              className="p-1 hover:bg-accent rounded transition-colors"
              title="Open map in new tab"
            >
              <ExternalLink className="w-3 h-3 text-muted-foreground" />
            </button>
            <button onClick={() => setIsMinimized(true)} className="p-1 hover:bg-accent rounded transition-colors">
              <Minimize2 className="w-3 h-3 text-muted-foreground" />
            </button>
            {onClose && (
              <button onClick={onClose} className="p-1 hover:bg-destructive/10 rounded transition-colors">
                <X className="w-3 h-3 text-muted-foreground" />
              </button>
            )}
          </div>
        )}
      </div>

      {/* Active mode hint */}
      {activeMode !== 'none' && (
        <div className="px-3 py-1 bg-primary/5 border-b border-primary/20 flex items-center justify-between flex-shrink-0 relative z-[10]">
          <p className="text-[10px] text-primary font-medium">
            {activeMode === 'marker' && 'Click the map to place a marker'}
            {activeMode === 'circle' && 'Click and drag to draw a circle radius'}
            {activeMode === 'polyline' && 'Click to add route points. Double-click to finish.'}
            {activeMode === 'polygon' && 'Click to add zone vertices. Double-click to close the shape.'}
          </p>
          <button
            onClick={cancelCurrentDraw}
            className="text-[10px] text-muted-foreground hover:text-foreground font-medium"
          >
            Cancel
          </button>
        </div>
      )}

      {/* Map container — floating panels live here so they sit above the map */}
      <div className="flex-1 min-h-0 relative">
        {showOverlayPanel && (
          <div className="absolute top-2 left-2 z-[1000] w-80 max-w-[calc(100vw-1rem)] bg-card/95 backdrop-blur border border-border rounded-lg shadow-xl p-3">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[10px] font-bold text-foreground uppercase">Site map overlay</span>
              <button onClick={() => setShowOverlayPanel(false)} className="p-0.5 hover:bg-accent rounded">
                <X className="w-2.5 h-2.5 text-muted-foreground" />
              </button>
            </div>
            <MapOverlayControls
              overlay={mapOverlay}
              defaultCenter={eventMapCenter ?? undefined}
              getMap={() => mapRef.current}
              onChange={handleOverlayChange}
              saving={overlaySaving}
              compact
            />
          </div>
        )}

        {showDrawingList && (
          <div className="absolute top-2 right-2 z-[1000] w-64 bg-card/95 backdrop-blur border border-border rounded-lg shadow-xl">
          <div className="px-3 py-2 border-b border-border flex items-center justify-between">
            <span className="text-[10px] font-bold text-foreground uppercase">Map Key</span>
            <button onClick={() => setShowDrawingList(false)} className="p-0.5 hover:bg-accent rounded">
              <X className="w-2.5 h-2.5 text-muted-foreground" />
            </button>
          </div>
          
          <div className="max-h-96 overflow-y-auto">
            {/* Incidents Section */}
            <div className="border-b border-border">
              <button
                onClick={() => setExpandedSections(prev => ({ ...prev, incidents: !prev.incidents }))}
                className="w-full px-3 py-2 flex items-center justify-between hover:bg-accent/50 transition-colors"
              >
                <span className="text-[10px] font-semibold text-foreground">Incidents</span>
                <span className={cn(
                  'w-3 h-3 transition-transform',
                  expandedSections.incidents ? 'rotate-90' : ''
                )}>
                  ▶
                </span>
              </button>
              
              {expandedSections.incidents && (
                <div className="px-3 pb-2 space-y-1.5">
                  {Object.entries(severityColors).map(([severity, color]) => {
                    const count = incidents.filter(inc => inc.severity === severity).length;
                    return (
                      <div key={severity} className="flex items-center gap-2 text-[10px]">
                        <div className="w-3 h-3 rounded-full border-2 border-white shadow-sm" style={{ backgroundColor: color }} />
                        <span className="text-foreground capitalize">{severity}</span>
                        {count > 0 && <span className="text-muted-foreground">({count})</span>}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Drawings Section */}
            <div className="border-b border-border">
              <button
                onClick={() => setExpandedSections(prev => ({ ...prev, drawings: !prev.drawings }))}
                className="w-full px-3 py-2 flex items-center justify-between hover:bg-accent/50 transition-colors"
              >
                <span className="text-[10px] font-semibold text-foreground">Drawings ({drawings.length})</span>
                <span className={cn(
                  'w-3 h-3 transition-transform',
                  expandedSections.drawings ? 'rotate-90' : ''
                )}>
                  ▶
                </span>
              </button>
              
              {expandedSections.drawings && (
                <div className="px-3 pb-2 space-y-1">
                  {drawings.length === 0 ? (
                    <div className="text-[10px] text-muted-foreground py-2">No drawings yet</div>
                  ) : (
                    drawings.map((d) => (
                      <div key={d.id} className="flex items-start gap-2 py-1 group">
                        <span className={cn(
                          'w-2 h-2 rounded-full flex-shrink-0 mt-0.5',
                          d.type === 'marker' ? 'bg-blue-400' :
                          d.type === 'circle' ? 'bg-blue-400' :
                          d.type === 'polyline' ? 'bg-orange-400' : 'bg-purple-400'
                        )} />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1">
                            <input
                              type="text"
                              value={d.label || d.type}
                              onChange={(e) => {
                                setDrawings(prev => prev.map(item => 
                                  item.id === d.id ? { ...item, label: e.target.value } : item
                                ));
                              }}
                              className="text-[10px] font-medium text-foreground bg-transparent border-b border-transparent hover:border-border focus:border-primary focus:outline-none flex-1 min-w-0"
                            />
                          </div>
                          <textarea
                            value={d.notes || ''}
                            onChange={(e) => {
                              setDrawings(prev => prev.map(item => 
                                item.id === d.id ? { ...item, notes: e.target.value } : item
                              ));
                            }}
                            placeholder="Add notes..."
                            className="text-[9px] text-muted-foreground bg-transparent border-b border-transparent hover:border-border focus:border-primary focus:outline-none w-full resize-none mt-0.5"
                            rows={1}
                          />
                          <div className="flex gap-1 mt-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button
                              onClick={() => {
                                // Focus on the drawing
                                if (d.type === 'marker') {
                                  const marker = d.layer as L.Marker;
                                  const latlng = marker.getLatLng();
                                  mapRef.current?.flyTo([latlng.lat, latlng.lng], 16);
                                  marker.openPopup();
                                } else if (d.type === 'circle') {
                                  const circle = d.layer as L.Circle;
                                  mapRef.current?.flyTo(circle.getLatLng(), 15);
                                  circle.openPopup();
                                } else if (d.type === 'polyline' || d.type === 'polygon') {
                                  const bounds = (d.layer as L.Polyline).getBounds();
                                  mapRef.current?.fitBounds(bounds, { padding: [20, 20] });
                                  d.layer.openPopup();
                                }
                              }}
                              className="text-[9px] text-primary hover:text-primary/80"
                            >
                              Focus
                            </button>
                            <button
                              onClick={() => removeDrawing(d.id)}
                              className="text-[9px] text-destructive hover:text-destructive/80"
                            >
                              Delete
                            </button>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>

            {/* Tracking Section */}
            <div>
              <button
                onClick={() => setExpandedSections(prev => ({ ...prev, tracking: !prev.tracking }))}
                className="w-full px-3 py-2 flex items-center justify-between hover:bg-accent/50 transition-colors"
              >
                <span className="text-[10px] font-semibold text-foreground">Unit Tracking</span>
                <span className={cn(
                  'w-3 h-3 transition-transform',
                  expandedSections.tracking ? 'rotate-90' : ''
                )}>
                  ▶
                </span>
              </button>
              
              {expandedSections.tracking && (
                <div className="px-3 pb-2 space-y-1.5">
                  <div className="flex items-center gap-2 text-[10px]">
                    <div className="w-3 h-3 rounded-full bg-green-400 border-2 border-white"></div>
                    <span className="text-foreground">Available</span>
                  </div>
                  <div className="flex items-center gap-2 text-[10px]">
                    <div className="w-3 h-3 rounded-full bg-amber-400 border-2 border-white"></div>
                    <span className="text-foreground">En Route</span>
                  </div>
                  <div className="flex items-center gap-2 text-[10px]">
                    <div className="w-3 h-3 rounded-full bg-red-400 border-2 border-white"></div>
                    <span className="text-foreground">On Scene</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        )}

        <div ref={containerRef} className="absolute inset-0 z-0" />
        {activeEventId && eventLoading && (
          <div className="absolute inset-0 z-[20] flex items-center justify-center bg-background/90">
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Loader2 className="w-4 h-4 animate-spin text-primary" />
              Loading map…
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
